import React from 'react';
import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import config from '../../../../config/config';

const ContentCarousel = ({ section }) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  if (!section || !section.carouselItems) return null;
  
  const itemsPerPage = isMobile ? 1 : 4;
  const carouselItems = section.carouselItems;
  const maxIndex = Math.max(0, carouselItems.length - itemsPerPage);

  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };

  const visibleProducts = carouselItems.slice(currentIndex, currentIndex + itemsPerPage);

  return (
      <Box sx={{ py: 6 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          sx={{ 
            fontWeight: 'bold',
            mb: 5,
            color: '#2c3e50'
          }}
        >
          {section.title}
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {visibleProducts.map((product, index) => {
            const productLink = product.link;
            const cardComponent = (
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                  },
                  ...(productLink && {
                    cursor: 'pointer',
                    textDecoration: 'none',
                    color: 'inherit'
                  })
                }}
              >
                <Box
                  sx={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '0px 0px 4px 4px'   
                  }}
                >
                  {product.itemMedia?.formats && (
                    <img
                      src={`${config.CMS_BASE_URL}${
                        product.itemMedia.formats.large?.url || 
                        product.itemMedia.formats.medium?.url || 
                        product.itemMedia.formats.small?.url
                      }`}
                      alt={product.titleItem}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </Box>
                
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    sx={{ 
                      fontWeight: 'bold',
                      mb: 2,
                      color: '#2c3e50'
                    }}
                  >
                    {product.titleItem}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      lineHeight: 1.6,
                      color: '#7f8c8d'
                    }}
                  >
                    {product.description}
                  </Typography>
                </CardContent>
              </Card>
            );

            return (
              <Grid item xs={12} sm={6} md={3} key={product.id}>
                {productLink ? (
                  <RouterLink 
                    to={productLink} 
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    {cardComponent}
                  </RouterLink>
                ) : (
                  cardComponent
                )}
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <IconButton 
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            sx={{ 
              backgroundColor: '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&.Mui-disabled': {
                backgroundColor: '#e9ecef',
                opacity: 0.5
              },
              '&:hover': {
                backgroundColor: '#e9ecef',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <ChevronLeft sx={{ color: '#6c757d'}} />
          </IconButton>
          <IconButton 
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
            sx={{ 
              backgroundColor: '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&.Mui-disabled': {
                backgroundColor: '#e9ecef',
                opacity: 0.5
              },
              '&:hover': {
                backgroundColor: '#e9ecef',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <ChevronRight sx={{ color: '#6c757d' }} />
          </IconButton>
        </Box>
      </Box>
  );
};

export default ContentCarousel;
